import React, { Fragment } from 'react';
import { scaleLinear } from 'd3-scale';
import { timeHour } from 'd3-time';

import AGPGraph from './AGPGraph';
import AGPLabels from './AGPLabels';
import BasalGraph from './BasalGraph';
import BaseSVG from './BaseSVG';
import BolusHexBin from './BolusHexBin';
import BolusScatterPlot from './BolusScatterPlot';
import D3Component from './D3Component';
import GlucoseSVG from './GlucoseSVG';
import MainView from './MainView';
import { parseDate } from '../utils/formats';
import YAxis from './YAxis';
import './Dashboard.css';

const debug = (process.env.NODE_ENV !== 'production') && false;

const bolusPlot = BolusHexBin;

function pointyHexagon(radius = 5) {
  const thirdPi = Math.PI / 3;
  const angles = [0, thirdPi, 2 * thirdPi, 3 * thirdPi, 4 * thirdPi, 5 * thirdPi];
  let x0 = 0;
  let y0 = 0;
  const points = angles.map(angle => {
    const x1 = -Math.sin(angle) * radius;
    const y1 = Math.cos(angle) * radius;
    const dx = x1 - x0;
    const dy = y1 - y0;
    x0 = x1;
    y0 = y1;
    return [dx, dy];
  });
  return `m${points.join('l')}z`;
  }
  
function flatHexagon(r = 5) {
  const v = -Math.cos(60) * r;
  // return `m${-r},0 l${r / 2},${-v} h${r} l${r / 2},${-v} l${-r / 2},${-v} h${-r} z`;
  return `m${-r},0 l${r / 2},${-v} h${r} l${r / 2},${v} ${-r / 2},${v} h${-r} z`;
}
  

function InsulinReport(props) {
  const {
    days,
    sgvData = {},
    bolusData = {},
    bolusFocus,
    basalData = {},
    formatDateRange,
    formatGlucoseValue,
    interactive = true,
    percentile,
    profiles = {},
    selectBolusFocus,
    t,
  } = props;

  const firstDay = parseDate(Object.keys(sgvData).sort()[0]);
  const times = [firstDay, timeHour.offset(firstDay, 24)];

  if (debug) {
    console.log('Basal data in Insulin Report', basalData);
  }

  return (
    <MainView
      description={t('insulin.main.description')}
      heading={t('insulin.main.heading')}
      interactive={interactive}
      key="mainView"
      title={`Sensotrend Insulin Report ${formatDateRange(days)}`}
    >
      <section>
        <GlucoseSVG
          key="mainGraph"
          times={[firstDay, timeHour.offset(firstDay, 24)]}
          yLabel={<AGPLabels t={t}/>}
        >
          <D3Component graph={AGPGraph} data={sgvData} formatGlucoseValue={formatGlucoseValue} percentile={percentile} t={t}/>
        </GlucoseSVG>
      </section>
      <section>
        <BaseSVG
          key="bolusGraphSVG"
          times={times}
          height={170}
          yLabel={
            <g className="scatterplot bolus labels" transform="translate(10, 85)">
              {bolusPlot === BolusHexBin
              ? (
                <Fragment>
                  <a
                    href={`?showBolus=${!bolusFocus ? 'meal' : (bolusFocus === 'meal' ? 'correction' : 'all')}`}
                    onClick={(e) => {
                      e.preventDefault();
                      console.log('Change bolus focus from', bolusFocus);
                      selectBolusFocus(!bolusFocus ? 'meal' : (bolusFocus === 'meal' ? 'correction' : undefined));
                    }}
                  >
                    <g transform="translate(0, -30)">
                      <circle className="meal bolus entry legend" r="3" />
                      <text dx="15" dy="0.35em">Meals</text>
                    </g>
                  </a>
                  <g transform="translate(0, -10)">
                    <path className="meal bolus entry legend" d={flatHexagon(8)} />
                    <text dx="15" dy="0.35em">1</text>
                  </g>
                  <g transform="translate(0, 10)">
                    <path className="meal bolus double entry legend" d={flatHexagon(8)} />
                    <text dx="15" dy="0.35em">2</text>
                  </g>
                  <g transform="translate(0, 30)">
                    <path className="meal bolus multi entry legend" d={flatHexagon(8)} />
                    <text dx="15" dy="0.35em">&gt; 2</text>
                  </g>
                </Fragment>
              )
              : (
                <Fragment>
                  <a
                    href="?showBolus=meal"
                    onClick={(e) => {
                      e.preventDefault();
                      selectBolusFocus(bolusFocus !== 'meal' ? 'meal' : undefined);
                    }}
                  >
                    <g transform="translate(3, -15)">
                      <rect className={`${bolusFocus === 'meal' ? 'active ' : ''}selector`} x="-10" y="-11" width="76" height="22" />
                      <circle r="8" className="meal bolus entry inactive legend" />
                      <circle r="2" className="meal bolus indicator inactive legend" />
                      <text className="inactive" dx="10" dy="0.35em">Meal</text>
                    </g>
                  </a>
                  <a
                    href="?showBolus=correction"
                    onClick={(e) => {
                      e.preventDefault();
                      selectBolusFocus(bolusFocus !== 'correction' ? 'correction' : undefined);
                    }}
                  >                  
                    <g transform="translate(3, 15)">
                    <rect className={`${bolusFocus === 'correction' ? 'active ' : ''}selector`} x="-10" y="-11" width="76" height="22" />
                      <circle r="8" className="correction bolus entry inactive legend" />
                      <text className="inactive" dx="10" dy="0.35em">Correction</text>
                    </g>
                  </a>
                </Fragment>
              )}
            </g>
          }
          yAxis={<YAxis
            scale={scaleLinear().domain([0, 20]).range([170, 0])}
            tickFormat={t => ((isNaN(t) || (t % 4)) ? undefined : t)}
            legend={
              <text
                className="legend" 
                transform="translate(-42,55) rotate(-90)"
              >
                {`${t('insulin.main.bolusDoses')} (${t('insulin.iu')})`}
              </text>
            }
          />}
        >
          <D3Component key="bolusGraph" data={bolusData} focus={bolusFocus} graph={bolusPlot} t={t} />
        </BaseSVG>
      </section>
      <section>
        {Object.values(profiles).map((p, i) => (
          <Fragment key={`basalGraph-profile${i}`}>
            <h3 className="tab">{p.name}</h3>
            <BaseSVG
              times={times}
              height={170}
              yAxis={
                <YAxis
                  scale={scaleLinear().domain([0, 3]).range([170, 0])}
                  tickFormat={f => ((isNaN(f) || (f % 1)) ? undefined : f.toFixed(1))}
                  legend={
                    <text
                      className="legend" 
                      transform="translate(-42,55) rotate(-90)"
                    >
                      {`${t('insulin.main.basalDoses')} (${t('insulin.iu/h')})`}
                    </text>
                  }
                />
              }
            >
              <D3Component key="basalGraph" data={basalData} profile={p} graph={BasalGraph} t={t} />
            </BaseSVG>

          </Fragment>
        ))}
      </section>
    </MainView>
  );
}

export default InsulinReport;
